"use client";
import React from "react";
import { useRouter, usePathname, notFound } from "next/navigation";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import dynamic from "next/dynamic";

const Player = dynamic(() => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player), { ssr: false, });
import NextImage from "next/image";
import Typography from "@mui/material/Typography";
import Link from "@AdbhutByte/utils/components/Link";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import PrivacyPolicy from "@AdbhutByte/app/mdx/policies/privacy.mdx";
import ToS from "@AdbhutByte/app/mdx/policies/tos.mdx";
import CookiePolicy from "@AdbhutByte/app/mdx/policies/cookie.mdx";
import CopyrightNotice from "@AdbhutByte/app/mdx/policies/copy.mdx";
import ExternalLinkPolicy from "@AdbhutByte/app/mdx/policies/eld.mdx";
import DisputePolicy from "@AdbhutByte/app/mdx/policies/drp.mdx";
import {
  SITE_FALLBACK_NAME,
  SITE_FALLBACK_URL,
} from "@AdbhutByte/utils/universalModifiers";

const MuiPlayer = styled(Player)({});
const Image = styled(NextImage)({});

const baseUrl = process.env.NEXT_PUBLIC_URL ?? SITE_FALLBACK_URL;
const baseName = process.env.NEXT_PUBLIC_SITE_NAME ?? SITE_FALLBACK_NAME;

interface PoliciesProps {
  policyName: string;
}

type PoliciesDBProps = Array<{
  name: string;
  slug: string;
  link: string;
  lastUpdated: string;
}>;

export const policies = [
  {
    name: "Privacy Policy",
    slug: "privacy-policy",
    link: "policies/privacy-policy",
    lastUpdated: "July 25, 2023",
  },
  {
    name: "Terms and Conditions",
    slug: "terms-and-conditions",
    link: "policies/terms-and-conditions",
    lastUpdated: "July 25, 2023",
  },
  {
    name: "Cookie Policy",
    slug: "cookie-policy",
    link: "policies/cookie-policy",
    lastUpdated: "July 25, 2023",
  },
  {
    name: "Copyright Notice",
    slug: "copyright-notice",
    link: "policies/copyright-notice",
    lastUpdated: "July 25, 2023",
  },
  {
    name: "External Links Disclaimer",
    slug: "external-link-disclaimer",
    link: "policies/external-link-disclaimer",
    lastUpdated: "July 25, 2023",
  },
  {
    name: "Dispute Resolution Policy",
    slug: "dispute-resolution-policy",
    link: "policies/dispute-resolution-policy",
    lastUpdated: "July 25, 2023",
  },
];

export async function generateStaticParams() {
  const dbPolicies: PoliciesDBProps = policies;

  return dbPolicies.map((dbPolicy) => ({
    policyName: dbPolicy.name,
  }));
}

const Policies = ({ policyName }: PoliciesProps) => {
  policyName = policyName.toLocaleLowerCase();
  const router = useRouter();
  const pathname = usePathname();
  const [selectedPolicy, setSelectedPolicy] = React.useState<string | false>(
    false
  );
  const [lastUpdatedPolicy, setLastUpdatedPolicy] = React.useState<
    string | false
  >(false);
  React.useEffect(() => {
    if (!selectedPolicy && !lastUpdatedPolicy) {
      let matchFound = false;
      policies.forEach((policy) => {
        if (policy.slug === policyName) {
          setSelectedPolicy(policy.name);
          setLastUpdatedPolicy(policy.lastUpdated);
          matchFound = true;
        }
      });
      if (!matchFound) {
        notFound();
      }
    }
  }, [router, pathname, selectedPolicy, policyName, lastUpdatedPolicy]);
  return (
    <Container maxWidth={false}>
      <Box pb={5} pt={10}>
        {(() => {
          if (selectedPolicy && lastUpdatedPolicy) {
            if (policyName === "privacy-policy") {
              return (
                <React.Fragment>
                  <Typography variant="h4">
                    {selectedPolicy} for {new URL(baseUrl).hostname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated: {lastUpdatedPolicy}
                  </Typography>
                  <Divider />
                  <Typography component="div" variant="body1">
                    <PrivacyPolicy />
                  </Typography>
                </React.Fragment>
              );
            } else if (policyName === "terms-and-conditions") {
              return (
                <React.Fragment>
                  <Typography variant="h4">
                    {selectedPolicy} for {new URL(baseUrl).hostname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated: {lastUpdatedPolicy}
                  </Typography>
                  <Divider />
                  <Typography component="div" variant="body1">
                    <ToS />
                  </Typography>
                </React.Fragment>
              );
            } else if (policyName === "cookie-policy") {
              return (
                <React.Fragment>
                  <Typography variant="h4">
                    {selectedPolicy} for {new URL(baseUrl).hostname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated: {lastUpdatedPolicy}
                  </Typography>
                  <Divider />
                  <Typography component="div" variant="body1">
                    <CookiePolicy />
                  </Typography>
                </React.Fragment>
              );
            } else if (policyName === "external-link-disclaimer") {
              return (
                <React.Fragment>
                  <Typography variant="h4">
                    {selectedPolicy} for {new URL(baseUrl).hostname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated: {lastUpdatedPolicy}
                  </Typography>
                  <Divider />
                  <Typography component="div" variant="body1">
                    <ExternalLinkPolicy />
                  </Typography>
                </React.Fragment>
              );
            } else if (policyName === "copyright-notice") {
              return (
                <React.Fragment>
                  <Typography variant="h4">
                    {selectedPolicy} for {new URL(baseUrl).hostname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated: {lastUpdatedPolicy}
                  </Typography>
                  <Divider />
                  <Typography component="div" variant="body1">
                    <CopyrightNotice />
                  </Typography>
                </React.Fragment>
              );
            } else if (policyName === "dispute-resolution-policy") {
              return (
                <React.Fragment>
                  <Typography variant="h4">
                    {selectedPolicy} for {new URL(baseUrl).hostname}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated: {lastUpdatedPolicy}
                  </Typography>
                  <Divider />
                  <Typography component="div" variant="body1">
                    <DisputePolicy />
                  </Typography>
                </React.Fragment>
              );
            }
          }
        })()}
      </Box>
    </Container>
  );
};

export default Policies;
